import dateFormatterDD_MM_YYYY from "../../../utils/DateFormatterDD_MM_YYYY";
import { placeholderImage, normalizeImageUrl } from "./image";

export {
  API_BOOKING_DETAILS_BASE,
  API_FETCH_BOOKINGS,
  API_LISTING_DETAILS_BASE,
  buildBookingDetailsUrl,
  buildListingDetailsUrl,
} from "../services/bookingAPI";

export { placeholderImage, normalizeImageUrl };

const E164_PHONE_REGEX = /^\+[1-9]\d{7,14}$/;
const FAMILY_PATTERN = /(\d+)\s*adult[s]?\s*-\s*(\d+)\s*kid[s]?/i;

const ARRIVAL_KEYS = ["arrivaldate", "arrival_date", "arrivalDate"];
const DEPARTURE_KEYS = ["departuredate", "departure_date", "departureDate"];
const TOTAL_KEYS = [
  "totalprice",
  "total_price",
  "totalPrice",
  "total",
  "amount",
  "paymentAmount",
  "payment_amount",
];

const pickFirst = (obj, keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value != null && value !== "") return value;
  }
  return null;
};

const pickFirstNumber = (obj, keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value == null || value === "") {
      continue;
    }

    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) {
      return numericValue;
    }
  }

  return null;
};

const startOfDay = (date) => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

export const isValidPhoneE164 = (phoneNumber = "") => E164_PHONE_REGEX.test(phoneNumber);

export const clamp = (value, min = 0, max = 20) => {
  if (value < min) return min;
  if (value > max) return max;
  return value;
};

export const limitBetween = clamp;

export const parseFamilyString = (familyString = "") => {
  const text = String(familyString);
  const match = text.match(FAMILY_PATTERN);

  if (!match) return { adults: 0, kids: 0 };

  const adults = Number(match[1]);
  const kids = Number(match[2]);

  return { adults, kids };
};

export const formatFamilyLabel = ({ adults = 0, kids = 0 }) =>
  `${adults} adult${adults === 1 ? "" : "s"} - ${kids} kid${kids === 1 ? "" : "s"}`;

export const toDate = (rawValue) => {
  if (rawValue == null) return null;

  const numericValue = Number(rawValue);

  if (Number.isFinite(numericValue)) {
    const isSeconds = String(Math.trunc(numericValue)).length <= 10;
    const ms = isSeconds ? numericValue * 1000 : numericValue;

    const date = new Date(ms);
    return isNaN(date) ? null : date;
  }

  const date = new Date(rawValue);
  return isNaN(date) ? null : date;
};

export const getArrivalDate = (booking) => toDate(pickFirst(booking, ARRIVAL_KEYS));
export const getDepartureDate = (booking) => toDate(pickFirst(booking, DEPARTURE_KEYS));
export const getBookingId = (booking) =>
  booking?.id ??
  booking?.ID ??
  booking?.bookingId ??
  booking?.paymentid ??
  booking?.paymentId ??
  null;

export const getReservationNumber = (booking) =>
  booking?.paymentid ??
  booking?.paymentId ??
  getBookingId(booking) ??
  "-";

export const getBookingCreatedAt = (booking) =>
  toDate(booking?.createdat ?? booking?.createdAt ?? booking?.bookedAt ?? booking?.bookingDate);

export const getBookingTotal = (booking) => pickFirstNumber(booking, TOTAL_KEYS);

export const splitBookingsByTime = (bookings) => {
  const today = startOfDay(new Date());

  const weekAhead = new Date(today);
  weekAhead.setDate(today.getDate() + 7);

  const currentBookings = [];
  const upcomingBookings = [];
  const pastBookings = [];

  bookings.forEach((booking) => {
    const arrivalDate = getArrivalDate(booking);
    const departureDate = getDepartureDate(booking);

    if (!arrivalDate || !departureDate) {
      upcomingBookings.push(booking);
      return;
    }

    const arrival = startOfDay(arrivalDate);
    const departure = startOfDay(departureDate);

    if (departure < today) {
      pastBookings.push(booking);
      return;
    }

    const isOngoing = arrival <= today && departure >= today;
    const startsThisWeek = arrival > today && arrival <= weekAhead;

    if (isOngoing || startsThisWeek) currentBookings.push(booking);
    else upcomingBookings.push(booking);
  });

  return { currentBookings, upcomingBookings, pastBookings };
};

export const normalizeGuestBookingsResponse = (bookingData) => {
  if (Array.isArray(bookingData)) return bookingData;
  if (Array.isArray(bookingData?.data)) return bookingData.data;
  if (Array.isArray(bookingData?.response)) return bookingData.response;
  if (typeof bookingData?.body === "string") {
    try {
      const innerParsed = JSON.parse(bookingData.body);
      if (Array.isArray(innerParsed)) return innerParsed;
      if (Array.isArray(innerParsed?.response)) return innerParsed.response;
    } catch {
      return [];
    }
  }
  return [];
};

export const getPaidBookings = (bookingData) =>
  normalizeGuestBookingsResponse(bookingData).filter(
    (booking) => String(booking?.status ?? booking?.Status ?? "").toLowerCase() === "paid"
  );

export const getInquiryBookings = (bookingData) =>
  normalizeGuestBookingsResponse(bookingData).filter((booking) => {
    const status = String(booking?.status ?? booking?.Status ?? "").toLowerCase();
    const bookingType = String(booking?.bookingtype ?? booking?.bookingType ?? "direct").toLowerCase();
    if (status === "inquiry" || status === "declined") return true;
    if (status === "awaiting payment" && bookingType === "inquiry") return true;
    return false;
  });

export const normalizeStayStatus = (value) => {
  const normalized = String(value || "").trim().toLowerCase();

  if (!normalized) {
    return "Confirmed";
  }

  if (["paid", "confirmed"].includes(normalized)) {
    return "Confirmed";
  }

  if (normalized === "cancelled" || normalized === "canceled") {
    return "Cancelled";
  }

  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

export const getCurrentOrUpcomingBooking = (bookingData) => {
  const paidBookings = getPaidBookings(bookingData);
  const { currentBookings, upcomingBookings } = splitBookingsByTime(paidBookings);
  return currentBookings[0] || upcomingBookings[0] || paidBookings[0] || null;
};

export const getInquiryStatusInfo = (status, bookingType) => {
  const s = String(status || "").toLowerCase();
  const t = String(bookingType || "direct").toLowerCase();

  if (s === "inquiry") {
    return {
      label: "Awaiting host response",
      description: "Your request has been sent. The host will accept or decline it shortly.",
      variant: "pending",
    };
  }
  if (s === "awaiting payment" && t === "inquiry") {
    return {
      label: "Accepted — payment required",
      description: "The host accepted your request. Complete your payment to confirm the booking.",
      variant: "action",
    };
  }
  if (s === "declined") {
    return {
      label: "Request declined",
      description: "The host has declined your request.",
      variant: "declined",
    };
  }
  return null;
};

export const resolveHostName = (...sources) => {
  for (const source of sources) {
    const value = String(source || "").trim();
    if (value) return value;
  }
  return "-";
};

export const resolveSubtitleCity = (subtitle = "") => {
  const normalizedSubtitle = String(subtitle || "").trim();
  return normalizedSubtitle ? normalizedSubtitle.split(",")[0].trim() : "";
};

export const getPropertyId = (booking) =>
  booking?.property_id ??
  booking?.propertyId ??
  booking?.PropertyID ??
  booking?.id ??
  booking?.ID ??
  null;

export const formatDate = (dateValue) => {
  if (!dateValue) return "-";
  return dateFormatterDD_MM_YYYY(dateValue);
};

